import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Skeleton, Typography } from "@mui/material";

import { getChatThreadRoute } from "../../../../../routers/routeUtils";
import { useUserProfile } from "../../../../User/api/queries/useUserProfile";
import { useUserDataFormatter } from "../../../../User/hooks/useUserDataFormatter";

import { Chat } from "../../../types/chat";
import { OnlineIcon } from "../../../assets/icons/chat-page-icons";
import { isSenderUserOnline } from "../../../utils/isSenderUserOnline";
import { formatSidebarChatItemMessageTimeAgo } from "../../../utils/formatSidebarChatItemMessageTimeAgo";

export const ChatItem = ({
  chat,
  ownUserId,
  onChatSelect,
}: {
  chat: Chat;
  ownUserId: number;
  onChatSelect?: () => void;
}) => {
  const { i18n } = useTranslation();

  const { lastMessage, participants, id, lastSeen } = chat;
  const sendToUserID = participants?.find((id) => id !== ownUserId);

  const { data: senderData, isLoading: isSenderDataLoading } = useUserProfile(String(sendToUserID));
  const IS_SEND_TO_USER_ONLINE = isSenderUserOnline(lastSeen[sendToUserID ?? ""]);
  const IS_SEEN = lastMessage.senderId === ownUserId || lastSeen[ownUserId!] != 0;

  const CHAT_LINK = getChatThreadRoute(id, sendToUserID);
  const { fullName, firstTwoChr, media, formattedInfo } = useUserDataFormatter(senderData);

  if (isSenderDataLoading) return <ChatItemLoader />;
  if (lastMessage.type !== "TEXT") return null;

  const handleChatClick = () => {
    if (onChatSelect) {
      onChatSelect();
    }
  };

  return (
    <Box sx={styles.chatItem}>
      <Box component={Link} to={CHAT_LINK} sx={styles.chatItemLink} onClick={handleChatClick}>
        <Box sx={styles.chatItemContent}>
          <Box sx={styles.chatItemAvatarWrapper}>
            <Avatar sx={styles.chatItemAvatar} src={media}>
              {firstTwoChr}
            </Avatar>
            {IS_SEND_TO_USER_ONLINE && <OnlineIcon sx={styles.onlineIcon} />}
          </Box>
          <Box sx={styles.chatItemInfo}>
            <Box sx={styles.chatItemInfoTop}>
              <Typography sx={styles.chatItemSenderName} title={fullName}>
                {fullName}
              </Typography>
              <Typography sx={styles.chatItemTimestamp}>
                {formatSidebarChatItemMessageTimeAgo(lastMessage?.createdAt as number, i18n.language)}
              </Typography>
            </Box>
            <Typography sx={styles.chatItemRole}>{formattedInfo(",")}</Typography>
            <Typography sx={styles.chatItemMessage(IS_SEEN)}>{lastMessage.data.text}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const ChatItemLoader = () => {
  return (
    <Box sx={styles.chatItem}>
      <Box sx={styles.chatItemContent}>
        <Box sx={styles.chatItemAvatarWrapper}>
          <Skeleton variant="circular" sx={styles.chatItemAvatar} />
        </Box>
        <Box sx={styles.chatItemInfo}>
          <Box sx={styles.chatItemInfoTop}>
            <Skeleton variant="text" width={100} sx={styles.chatItemSenderName} />
            <Skeleton variant="text" width={40} sx={styles.chatItemTimestamp} />
          </Box>
          <Skeleton variant="text" width={60} sx={styles.chatItemRole} />
          <Skeleton variant="text" width={160} sx={styles.chatItemMessage(false)} />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  chatItem: {
    display: "flex",
    flexDirection: "column",
    rowGap: "5px",
  },
  chatItemLink: {
    textDecoration: "none",
  },
  chatItemContent: {
    display: "flex",
    columnGap: "10px",
    marginBottom: "",
  },
  chatItemAvatarWrapper: {
    height: "fit-content",
    position: "relative",
  },
  chatItemAvatar: {
    width: "48px",
    height: "48px",
    fontSize: "18px",
  },
  onlineIcon: {
    position: "absolute",
    bottom: "-4px",
    right: "-4px",
    border: "4px solid #FFF",
    borderRadius: "50%",
  },
  chatItemInfo: {
    display: "flex",
    flexDirection: "column",
    width: "calc(100% - 58px)",
  },
  chatItemInfoTop: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
    gap: "5px",
  },
  chatItemSenderName: {
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "24px",
    color: "#008B07",
    maxWidth: "calc(100% - 80px)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chatItemTimestamp: {
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "normal",
    color: "#A0A0A0",
    textTransform: "capitalize",
    textAlign: "right",
  },
  chatItemRole: {
    marginBottom: "12px",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "normal",
    color: "#707070",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "90%",
  },
  chatItemMessage: (isSeen: boolean) => ({
    display: "block",
    maxWidth: "90%",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontWeight: isSeen ? 400 : 700,
    lineHeight: "normal",
    color: "#1C1C1C",
  }),
};
